import { saleDateOf } from '@/lib/utils/saleDate';
import type {
  Category,
  Customer,
  Expense,
  Payment,
  Product,
  PurchaseOrder,
  Review,
  Sale,
  StoreSettings,
  Supplier,
  User,
  WebsiteOrder,
} from '@/types/domain';
import { ROLES, ROLE_PERMISSIONS, type Permission } from '@/lib/config/constants';
import type { RoleDefinition } from '@/store/rolesStore';

const EDITABLE_BUILTIN_ROLES = new Set<string>([ROLES.MANAGER, ROLES.CASHIER]);

const BUILTIN_ROLES: RoleDefinition[] = Object.values(ROLES).map((r) => ({
  id: r,
  label: r.charAt(0).toUpperCase() + r.slice(1),
  permissions: ROLE_PERMISSIONS[r] ?? [],
  system: true,
  editable: EDITABLE_BUILTIN_ROLES.has(r),
}));

const slugify = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
import type { ID, Paginated, QueryParams } from '@/types/common';

const delay = (ms = 250) => new Promise((res) => setTimeout(res, ms));

function paginate<T>(items: T[], params?: QueryParams): Paginated<T> {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 20;
  const start = (page - 1) * pageSize;
  const data = items.slice(start, start + pageSize);
  return {
    data,
    total: items.length,
    page,
    pageSize,
    totalPages: Math.ceil(items.length / pageSize),
  };
}

function search<T>(items: T[], q: string, fields: (keyof T)[]): T[] {
  if (!q) return items;
  const lower = q.toLowerCase();
  return items.filter((it) =>
    fields.some((f) =>
      String(it[f] ?? '')
        .toLowerCase()
        .includes(lower),
    ),
  );
}

function filterSalesByParams(items: Sale[], params?: QueryParams): Sale[] {
  let out = items;
  if (params?.search) out = search(out, String(params.search), ['invoiceNumber']);
  if (params?.dateFrom) {
    const from = new Date(String(params.dateFrom));
    out = out.filter((s) => new Date(saleDateOf(s)) >= from);
  }
  if (params?.dateTo) {
    const to = new Date(String(params.dateTo));
    out = out.filter((s) => new Date(saleDateOf(s)) < to);
  }
  return out;
}

class MockState {
  users: User[] = [];
  products: Product[] = [];
  categories: Category[] = [];
  customers: Customer[] = [];
  suppliers: Supplier[] = [];
  sales: Sale[] = [];
  orders: WebsiteOrder[] = [];
  expenses: Expense[] = [];
  settings: StoreSettings = {
    storeName: 'My Store',
    currency: { code: 'KES', symbol: 'Ksh ', position: 'before' },
    defaultTaxRate: 0,
    taxInclusive: false,
    receipt: { showLogo: true, showBarcode: true },
    invoiceTerms:
      'Goods once sold cannot be returned.\nEquipment remains the property of the company until fully paid for.\nRental equipment must be returned in the condition it was received.',
  };
  customRoles: RoleDefinition[] = [];
  reviews: Review[] = [];
}

const db = new MockState();

export const mockApi = {
  async login(email: string, password: string) {
    await delay();
    const user = db.users.find((u) => u.email === email && u.isActive);
    if (!user || password !== 'Hardsecurity#123$$') {
      throw { message: 'Invalid email or password', status: 401 };
    }
    return {
      token: 'mock.jwt.' + btoa(JSON.stringify({ sub: user.id, role: user.role })),
      refreshToken: 'mock.refresh.' + user.id,
      user,
    };
  },

  async me(): Promise<User> {
    await delay(100);
    return db.users[0]!;
  },

  async listProducts(params?: QueryParams): Promise<Paginated<Product>> {
    await delay();
    let items = [...db.products];
    if (params?.search) items = search(items, String(params.search), ['name', 'sku', 'barcode']);
    if (params?.categoryId) items = items.filter((p) => p.categoryId === params.categoryId);
    const status = (params as { status?: string } | undefined)?.status;
    if (status === 'active') items = items.filter((p) => p.isActive);
    else if (status === 'disabled' || status === 'inactive')
      items = items.filter((p) => !p.isActive);
    return paginate(items, params);
  },

  async getProductByBarcode(barcode: string): Promise<Product | null> {
    await delay(50);
    return db.products.find((p) => p.barcode === barcode) ?? null;
  },

  async getProductById(id: number): Promise<Product> {
    await delay();
    const p = db.products.find((x) => x.id === id);
    if (!p) throw { message: 'Product not found', status: 404 };
    return p;
  },

  async lowStockProducts(): Promise<Product[]> {
    await delay();
    return db.products.filter((p) => p.trackStock && p.stockQty <= p.lowStockThreshold);
  },

  async createProduct(data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> {
    await delay();
    const newP: Product = {
      ...data,
      id: db.products.length + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.products.push(newP);
    return newP;
  },

  async updateProduct(id: number, patch: Partial<Product>): Promise<Product> {
    await delay();
    const idx = db.products.findIndex((p) => p.id === id);
    if (idx < 0) throw { message: 'Product not found', status: 404 };
    db.products[idx] = { ...db.products[idx]!, ...patch, updatedAt: new Date().toISOString() };
    return db.products[idx]!;
  },

  async deleteProduct(id: number): Promise<void> {
    await delay();
    db.products = db.products.filter((p) => p.id !== id);
  },

  async listCategories(): Promise<Category[]> {
    await delay();
    return db.categories;
  },

  async listReviews(productId: ID): Promise<Review[]> {
    await delay();
    return db.reviews
      .filter((r) => String(r.productId) === String(productId))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  },

  async deleteReview(id: number): Promise<void> {
    await delay();
    db.reviews = db.reviews.filter((r) => Number(r.id) !== id);
  },

  async listCustomers(params?: QueryParams): Promise<Paginated<Customer>> {
    await delay();
    let items = [...db.customers];
    if (params?.search)
      items = search(items, String(params.search), ['fullName', 'email', 'phone']);
    return paginate(items, params);
  },

  async listExpenses(params?: QueryParams & { category?: string }): Promise<Paginated<Expense>> {
    await delay();
    let items = [...db.expenses].sort((a, b) => (a.expenseDate < b.expenseDate ? 1 : -1));
    if (params?.search) items = search(items, String(params.search), ['description', 'reference']);
    if (params?.category) items = items.filter((e) => e.category === params.category);
    return paginate(items, params);
  },

  async createExpense(data: Partial<Expense>): Promise<Expense> {
    await delay();
    const now = new Date().toISOString();
    const expense: Expense = {
      id: db.expenses.length ? Math.max(...db.expenses.map((e) => Number(e.id))) + 1 : 1,
      category: data.category ?? 'other',
      description: data.description,
      amount: data.amount ?? 0,
      expenseDate: data.expenseDate ?? now,
      paymentMethod: data.paymentMethod,
      reference: data.reference,
      isPaid: false,
      createdAt: now,
      updatedAt: now,
    };
    db.expenses.unshift(expense);
    return expense;
  },

  async updateExpense(id: number, patch: Partial<Expense>): Promise<Expense> {
    await delay();
    const idx = db.expenses.findIndex((e) => Number(e.id) === id);
    if (idx < 0) throw { message: 'Expense not found', status: 404 };
    db.expenses[idx] = { ...db.expenses[idx]!, ...patch, updatedAt: new Date().toISOString() };
    return db.expenses[idx]!;
  },

  async deleteExpense(id: number): Promise<void> {
    await delay();
    db.expenses = db.expenses.filter((e) => Number(e.id) !== id);
  },

  async listSuppliers(): Promise<Supplier[]> {
    await delay();
    return db.suppliers;
  },

  async listSales(params?: QueryParams): Promise<Paginated<Sale>> {
    await delay();
    let items = filterSalesByParams([...db.sales], params);
    if (params?.status) items = items.filter((s) => s.status === params.status);
    const sort = String(params?.sort ?? '');
    items.sort((a, b) => {
      switch (sort) {
        case 'oldest':
          return saleDateOf(a) < saleDateOf(b) ? -1 : 1;
        case 'amount_high':
          return b.total - a.total;
        case 'amount_low':
          return a.total - b.total;
        default:
          return saleDateOf(a) < saleDateOf(b) ? 1 : -1;
      }
    });
    return paginate(items, params);
  },

  async listOrders(params?: QueryParams): Promise<Paginated<WebsiteOrder>> {
    await delay();
    let items = [...db.orders];
    if (params?.search)
      items = search(items, String(params.search), [
        'reference',
        'customerName',
        'customerPhone',
        'location',
      ]);
    const status = (params as { status?: string } | undefined)?.status;
    if (status) items = items.filter((o) => o.status === status);
    items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return paginate(items, params);
  },

  async markOrderPaid(id: ID): Promise<WebsiteOrder> {
    await delay();
    const order = db.orders.find((o) => String(o.id) === String(id));
    if (!order) throw { message: 'Order not found', status: 404 };
    if (order.status === 'converted') throw { message: 'Order already converted', status: 409 };
    order.paidAt = new Date().toISOString();
    order.paymentReference = order.paymentReference ?? `Website order ${order.reference}`;
    return order;
  },

  async deleteOrder(id: ID): Promise<void> {
    await delay();
    const idx = db.orders.findIndex((o) => String(o.id) === String(id));
    if (idx < 0) throw { message: 'Order not found', status: 404 };
    if (db.orders[idx].status === 'converted')
      throw { message: 'Order already converted', status: 409 };
    db.orders.splice(idx, 1);
  },

  async convertOrder(id: ID): Promise<Sale> {
    await delay();
    const order = db.orders.find((o) => String(o.id) === String(id));
    if (!order) throw { message: 'Order not found', status: 404 };
    if (order.status === 'converted') throw { message: 'Order already converted', status: 409 };
    if ((order.payMethod ?? 'cod') === 'cod' && !order.paidAt)
      throw { message: 'Mark this order as paid before converting it to a sale', status: 409 };

    const sale: Sale = {
      id: db.sales.length + 1,
      invoiceNumber: `INV-${String(db.sales.length + 1).padStart(3, '0')}`,
      status: 'completed',
      customerId: null,
      cashierId: 1,
      items: order.lines.map((l, i) => ({
        id: db.sales.length + 1 + i,
        productId: Number(l.id),
        sku: String(l.id),
        name: l.title,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        basePrice: l.unitPrice,
        taxRate: 0,
        discount: l.discount ?? 0,
        passthrough: l.offerType === 'rent',
        duration: l.duration,
        durationUnit: l.durationUnit,
      })),
      subtotal: order.lines.reduce((n, l) => {
        const dur = l.duration && l.duration > 0 ? l.duration : 1;
        return n + (l.offerType === 'rent' ? 0 : l.unitPrice * l.quantity * dur - (l.discount || 0));
      }, 0),
      total: order.total,
      passthroughTotal: order.lines.reduce(
        (n, l) => {
          const dur = l.duration && l.duration > 0 ? l.duration : 1;
          return n + (l.offerType === 'rent' ? l.unitPrice * l.quantity * dur - (l.discount || 0) : 0);
        },
        0,
      ),
      taxTotal: 0,
      discountTotal: order.lines.reduce((n, l) => n + (l.discount || 0), 0),
      amountPaid: order.total,
      changeDue: 0,
      payments: [
        {
          method: (order.payMethod ?? 'cod') === 'mpesa' ? 'mpesa' : 'cash',
          amount: order.total,
          reference:
            order.paymentReference ?? `Website order ${order.reference}`,
        },
      ],
      notes: `Website order ${order.reference}`,
      saleDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.sales.unshift(sale);
    order.status = 'converted';
    order.convertedSaleId = sale.id;
    order.convertedSale = sale;
    for (const item of sale.items) {
      const p = db.products.find((x) => x.id === item.productId);
      if (p && p.trackStock) p.stockQty -= item.quantity;
    }
    return sale;
  },

  async salesStats(params?: QueryParams) {
    await delay();
    const items = filterSalesByParams([...db.sales], params);
    const completed = items.filter((s) => s.status === 'completed');
    const refunds = items.filter(
      (s) => s.status === 'refunded' || s.status === 'partially_refunded',
    );
    const partial = items.filter((s) => s.status === 'partially_refunded');
    return {
      total: items.length,
      completedCount: completed.length,
      totalRevenue: completed.reduce((n, s) => n + s.total, 0),
      customTotal: completed.reduce((n, s) => n + (s.passthroughTotal ?? 0), 0),
      refundedCount: refunds.length,
      refundedRevenue: refunds.reduce((n, s) => n + s.total, 0),
      fullRefundCount: refunds.length - partial.length,
      partialRefundCount: partial.length,
    };
  },

  async createSale(sale: Omit<Sale, 'id' | 'createdAt' | 'updatedAt'>): Promise<Sale> {
    await delay();
    const newSale: Sale = {
      ...sale,
      id: db.sales.length + 1,
      saleDate: sale.saleDate ?? new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    db.sales.unshift(newSale);
    for (const item of newSale.items) {
      const p = db.products.find((x) => x.id === item.productId);
      if (p && p.trackStock) p.stockQty -= item.quantity;
    }
    return newSale;
  },

  async recordSalePayment(id: ID, payment: Payment): Promise<Sale> {
    await delay();
    const sale = db.sales.find((s) => String(s.id) === String(id));
    if (!sale) throw { message: 'Sale not found', status: 404 };
    if (sale.status !== 'credit') throw { message: 'This sale is not on credit', status: 409 };
    if (payment.amount <= 0)
      throw { message: 'Payment amount must be greater than zero', status: 400 };
    sale.payments = [...sale.payments, payment];
    const paid = Math.round(((sale.amountPaid ?? 0) + payment.amount + Number.EPSILON) * 100) / 100;
    if (paid + 0.005 >= sale.total) {
      sale.amountPaid = sale.total;
      sale.status = 'completed';
    } else {
      sale.amountPaid = paid;
    }
    sale.updatedAt = new Date().toISOString();
    return sale;
  },

  async listUsers(): Promise<User[]> {
    await delay();
    return db.users;
  },

  async listRoles(): Promise<RoleDefinition[]> {
    await delay(100);
    const overrides = new Map(
      db.customRoles
        .filter((r) => EDITABLE_BUILTIN_ROLES.has(r.id))
        .map((r) => [r.id, r.permissions]),
    );
    const builtins = BUILTIN_ROLES.map((r) =>
      overrides.has(r.id) ? { ...r, permissions: overrides.get(r.id)! } : r,
    );
    const custom = db.customRoles.filter((r) => !EDITABLE_BUILTIN_ROLES.has(r.id));
    return [...builtins, ...custom];
  },

  async createRole(input: { label: string; permissions: Permission[] }): Promise<RoleDefinition> {
    await delay();
    const id = slugify(input.label);
    if (!id) throw { message: 'Name must contain letters or numbers', status: 400 };
    if ([...BUILTIN_ROLES, ...db.customRoles].some((r) => r.id === id)) {
      throw { message: 'A role with this name already exists', status: 400 };
    }
    const role: RoleDefinition = {
      id,
      label: input.label.trim(),
      permissions: input.permissions ?? [],
      system: false,
      editable: true,
    };
    db.customRoles.push(role);
    return role;
  },

  async updateRole(id: string, permissions: Permission[]): Promise<RoleDefinition> {
    await delay();
    if (EDITABLE_BUILTIN_ROLES.has(id)) {
      const builtin = BUILTIN_ROLES.find((r) => r.id === id);
      const existing = db.customRoles.find((r) => r.id === id);
      if (existing) {
        existing.permissions = permissions;
        return { ...existing, system: true, label: builtin?.label ?? existing.label };
      }
      const override: RoleDefinition = {
        id,
        label: builtin?.label ?? id,
        permissions,
        system: true,
        editable: true,
      };
      db.customRoles.push(override);
      return override;
    }
    const role = db.customRoles.find((r) => r.id === id);
    if (!role) throw { message: 'Role not found', status: 404 };
    role.permissions = permissions;
    return role;
  },

  async deleteRole(id: string): Promise<void> {
    await delay();
    db.customRoles = db.customRoles.filter((r) => r.id !== id);
  },

  async createUser(input: { fullName: string; email: string; role: User['role'] }): Promise<User> {
    await delay();
    const now = new Date().toISOString();
    const user: User = {
      id: db.users.length ? Math.max(...db.users.map((u) => Number(u.id))) + 1 : 1,
      email: input.email,
      fullName: input.fullName,
      role: input.role,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    db.users.push(user);
    return user;
  },

  async dashboardSummary(range = 'today') {
    await delay();
    const today = new Date();
    const todayStr = today.toDateString();
    const todaySales = db.sales.filter((s) => new Date(saleDateOf(s)).toDateString() === todayStr);
    const yesterdayStr = new Date(today.getTime() - 86_400_000).toDateString();
    const yesterdaySales = db.sales.filter(
      (s) => new Date(saleDateOf(s)).toDateString() === yesterdayStr,
    );

    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const day = 86_400_000;
    const period = (() => {
      switch (range) {
        case '7d': {
          const start = new Date(startOfToday.getTime() - 6 * day);
          return {
            start,
            prevStart: new Date(start.getTime() - 7 * day),
            label: 'Last 7 days',
            rng: '7d',
            granularity: 'day' as const,
            buckets: 7,
          };
        }
        case '30d': {
          const start = new Date(startOfToday.getTime() - 29 * day);
          return {
            start,
            prevStart: new Date(start.getTime() - 30 * day),
            label: 'Last 30 days',
            rng: '30d',
            granularity: 'day' as const,
            buckets: 30,
          };
        }
        case '12m': {
          const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
          return {
            start,
            prevStart: new Date(today.getFullYear(), today.getMonth() - 23, 1),
            label: 'Last 12 months',
            rng: '12m',
            granularity: 'month' as const,
            buckets: 12,
          };
        }
        default:
          return {
            start: startOfToday,
            prevStart: new Date(startOfToday.getTime() - day),
            label: 'Today',
            rng: 'today',
            granularity: 'day' as const,
            buckets: 7,
          };
      }
    })();

    let periodRevenue = 0;
    let prevRevenue = 0;
    let periodOrders = 0;
    const periodCustomers = new Set<number | string>();
    db.sales.forEach((s) => {
      const t = new Date(saleDateOf(s));
      if (t >= period.start) {
        periodRevenue += s.total;
        periodOrders += 1;
        if (s.customerId) periodCustomers.add(s.customerId);
      } else if (t >= period.prevStart) {
        prevRevenue += s.total;
      }
    });
    const periodAvgOrder = periodOrders > 0 ? periodRevenue / periodOrders : 0;
    const periodChangePct =
      prevRevenue > 0
        ? ((periodRevenue - prevRevenue) / prevRevenue) * 100
        : periodRevenue > 0
          ? 100
          : 0;

    const bucketKey = (d: Date) =>
      period.granularity === 'month'
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const buckets = new Map<string, { revenue: number; orders: number; expenses: number }>();
    const seriesKeys: string[] = [];
    for (let i = period.buckets - 1; i >= 0; i--) {
      const d =
        period.granularity === 'month'
          ? new Date(today.getFullYear(), today.getMonth() - i, 1)
          : new Date(startOfToday.getTime() - i * day);
      const key = bucketKey(d);
      seriesKeys.push(key);
      buckets.set(key, { revenue: 0, orders: 0, expenses: 0 });
    }
    db.sales.forEach((s) => {
      const b = buckets.get(bucketKey(new Date(saleDateOf(s))));
      if (b) {
        b.revenue += s.total;
        b.orders += 1;
      }
    });
    db.expenses.forEach((e) => {
      const b = buckets.get(bucketKey(new Date(e.expenseDate)));
      if (b) b.expenses += e.amount;
    });
    const revenueVsExpenses = seriesKeys.map((key) => ({ date: key, ...buckets.get(key)! }));
    const productRevenue = new Map<number, { name: string; revenue: number; qty: number }>();
    db.sales
      .filter((s) => new Date(saleDateOf(s)) >= period.start)
      .forEach((s) => {
        s.items.forEach((it) => {
          const cur = productRevenue.get(Number(it.productId)) ?? {
            name: it.name,
            revenue: 0,
            qty: 0,
          };
          cur.revenue += it.unitPrice * it.quantity * (it.duration && it.duration > 0 ? it.duration : 1) - (it.discount || 0);
          cur.qty += it.quantity;
          productRevenue.set(Number(it.productId), cur);
        });
      });
    const topProducts = Array.from(productRevenue.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const totalRevenue = db.sales.reduce((s, x) => s + x.total, 0);
    const totalOrders = db.sales.length;
    const totalExpenses = db.expenses.reduce((s, x) => s + x.amount, 0);
    const totalPurchases = 0;

    return {
      todayRevenue: todaySales.reduce((s, x) => s + x.total, 0),
      todayOrders: todaySales.length,
      yesterdayRevenue: yesterdaySales.reduce((s, x) => s + x.total, 0),
      yesterdayOrders: yesterdaySales.length,
      todayCustomers: new Set(todaySales.map((s) => s.customerId).filter(Boolean)).size,
      avgOrderValue:
        todaySales.length > 0 ? todaySales.reduce((s, x) => s + x.total, 0) / todaySales.length : 0,
      totalRevenue,
      totalOrders,
      totalAvgOrder: totalOrders > 0 ? totalRevenue / totalOrders : 0,
      totalExpenses,
      expenseCount: db.expenses.length,
      totalPurchases,
      netProfit: totalRevenue - (totalPurchases + totalExpenses),
      purchasesAreExpenses: true,
      totalCustomers: db.customers.length,
      totalProducts: db.products.length,
      range: period.rng,
      periodLabel: period.label,
      periodRevenue,
      periodOrders,
      periodCustomers: periodCustomers.size,
      periodAvgOrder,
      periodChangePct,
      chartGranularity: period.granularity,
      revenueVsExpenses,
      topProducts,
      recentSales: db.sales.slice(0, 6),
      recentPurchases: [] as PurchaseOrder[],
      recentExpenses: [...db.expenses]
        .sort((a, b) => (a.expenseDate < b.expenseDate ? 1 : -1))
        .slice(0, 5),
      lowStockCount: db.products.filter((p) => p.trackStock && p.stockQty <= p.lowStockThreshold)
        .length,
    };
  },

  async getSettings(): Promise<StoreSettings> {
    await delay();
    return db.settings;
  },

  async updateSettings(patch: Partial<StoreSettings>): Promise<StoreSettings> {
    await delay();
    db.settings = { ...db.settings, ...patch };
    return db.settings;
  },
};

export type DashboardSummary = Awaited<ReturnType<typeof mockApi.dashboardSummary>>;
